import axios, { type AxiosInstance, isAxiosError } from "axios";
import i18n from "../locales/i18n";
import { config } from "../config";
import type {
  IWidgetSession,
  ITermsBlock,
} from "../shared/session/interfaces/session";
import type {
  FundingBarcodeRefreshReason,
  ICashDepositContext,
  IFundingInstrument,
  IFundingMerchant,
  IFundingPolicy,
  IFundingStore,
  IFundingStoresQuery,
  IWidgetOperation,
} from "../interfaces/cash-deposit";
import { LINK_ERROR_CODE, RequestStatus } from "../utils/commons";
import { resolveUrlLinkId } from "../utils/resolveUrlLinkId";
import { resolveXLinkIdHeader } from "./resolveXLinkIdHeader";
import { useSessionStore } from "../store/useSessionStore";
import { useOfflineStore } from "../store/useOfflineStore";
import { isAxiosNetworkFailure } from "../utils/axiosNetworkError";

/**
 * Error class for link claim errors
 */
export class LinkClaimError extends Error {
  constructor(
    message: string,
    public code: LINK_ERROR_CODE,
    public statusCode?: number,
    public details?: unknown,
  ) {
    super(message);
    this.name = "LinkClaimError";
  }
}

export interface WidgetApiError {
  code: string;
  message: string;
  details?: unknown;
}

/** Extracts the normalized `{ code, message }` payload from a widget API error. */
export const getWidgetApiError = (error: unknown): WidgetApiError | null => {
  if (!isAxiosError(error)) return null;
  const body = error.response?.data?.error;
  if (!body || typeof body.code !== "string") return null;
  return {
    code: body.code,
    message: typeof body.message === "string" ? body.message : "",
    details: body.details,
  };
};

/**
 * Codes that make the link permanently unusable: the widget cannot recover from
 * them and must show the matching explanation instead of a generic error.
 */
const DEAD_LINK_CODES: string[] = [
  LINK_ERROR_CODE.LINK_NOT_FOUND,
  LINK_ERROR_CODE.LINK_EXPIRED,
  LINK_ERROR_CODE.LINK_REVOKED,
];

/**
 * Resolves the claim/API failure into a link error code. The backend `code`
 * wins over the HTTP status: a revoked link answers 409 `LINK_REVOKED` on
 * claim and 403 `LINK_REVOKED` on the partner CIP ticket, so status alone
 * cannot tell "revoked" from "already used".
 */
const resolveLinkErrorCode = (
  status: number | undefined,
  apiCode: string | undefined
): LINK_ERROR_CODE => {
  if (apiCode && apiCode in LINK_ERROR_CODE) {
    return apiCode as LINK_ERROR_CODE;
  }

  switch (status) {
    case 400:
      return LINK_ERROR_CODE.INVALID_TOKEN;
    case 404:
      return LINK_ERROR_CODE.LINK_NOT_FOUND;
    case 409:
      return LINK_ERROR_CODE.LINK_ALREADY_USED;
    case 410:
      return LINK_ERROR_CODE.LINK_EXPIRED;
    case 429:
      return LINK_ERROR_CODE.RATE_LIMITED;
    default:
      // No status means the request never reached the backend (network/CORS).
      return status === undefined || status >= 500
        ? LINK_ERROR_CODE.SERVICE_UNAVAILABLE
        : LINK_ERROR_CODE.INTERNAL_ERROR;
  }
};

/**
 * Custom event for dead links (LINK_NOT_FOUND, LINK_EXPIRED, LINK_REVOKED).
 * Dispatched when the API returns these errors on any widget endpoint.
 */
export const SESSION_EXPIRED_EVENT = "raas-widgets-flow-cash-deposit-session-expired";

export const MAINTENANCE_EVENT = "raas-widgets-flow-cash-deposit-maintenance";

export interface MaintenanceStatus {
  active: boolean;
  title?: string;
  body?: string;
  supportEmail?: string;
  supportWhatsappNumber?: string;
}
export const USER_BLOCKED_EVENT = "raas-widgets-flow-cash-deposit-user-blocked";

/**
 * True when the failure means the link itself died (expired / revoked / gone).
 * `useSessionExpiredHandler` already routes those to the explanatory screen, so
 * callers must not overwrite it with their own generic error copy.
 */
export const isDeadLinkError = (error: unknown): boolean => {
  if (!isAxiosError(error)) return false;
  const code = error.response?.data?.error?.code;
  return DEAD_LINK_CODES.includes(code) || error.response?.status === 410;
};

/**
 * Current link ID for API requests. Set after a successful claim.
 */
let currentLinkId: string | null = null;

/**
 * Set the current link ID for API requests (adds the x-link-id header).
 */
export const setWidgetLinkId = (linkId: string | null) => {
  currentLinkId = linkId;
};

export const getWidgetLinkId = (): string | null => currentLinkId;

export const probeLinkSession = async (
  linkId: string
): Promise<{ blocked: boolean; cipStatus?: string; expiresAt: string | null }> => {
  const { data } = await widgetApi.get(`/widget/links/${linkId}/session`, {
    raasSilentOffline: true,
  });
  const expiresAt = data?.data?.expiresAt;
  return {
    blocked: Boolean(
      data?.data?.userStatus?.blocked ?? data?.data?.user?.blocked
    ),
    cipStatus:
      data?.data?.userStatus?.cipStatus ?? data?.data?.user?.cipStatus,
    expiresAt: typeof expiresAt === "string" ? expiresAt : null,
  };
};

export const fetchMaintenanceStatus = async (
  linkId: string,
  locale: string,
): Promise<MaintenanceStatus> => {
  const { data } = await widgetApi.get(
    `/widget/links/${linkId}/maintenance`,
    { params: { locale }, raasSilentOffline: true },
  );
  const payload = data?.data;
  if (!payload?.active) return { active: false };
  return {
    active: true,
    title: typeof payload.title === "string" ? payload.title : undefined,
    body: typeof payload.body === "string" ? payload.body : undefined,
    supportEmail:
      typeof payload.supportEmail === "string" ? payload.supportEmail : undefined,
    supportWhatsappNumber:
      typeof payload.supportWhatsappNumber === "string"
        ? payload.supportWhatsappNumber
        : undefined,
  };
};

export const dispatchSessionExpiredEvent = (detail: {
  code: string;
  details?: unknown;
}): void => {
  window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT, { detail }));
};

function extractRequestLinkId(req: { headers?: unknown; url?: string } | undefined): string | null {
  if (!req) return null;
  const headers = req.headers as Record<string, unknown> | undefined;
  const header = headers?.["x-link-id"] ?? headers?.["X-Link-Id"];
  if (typeof header === "string" && header.length > 0) return header;
  const match = (req.url ?? "").match(/\/widget\/links\/([^/?]+)/);
  return match?.[1] ?? null;
}

export function shouldBroadcastSessionExpired(req: {
  headers?: unknown;
  url?: string;
} | undefined): boolean {
  if (useSessionStore.getState().session.status === RequestStatus.LOADING) {
    return false;
  }
  const reqLinkId = extractRequestLinkId(req);
  const urlLinkId = resolveUrlLinkId();
  if (reqLinkId && urlLinkId && reqLinkId !== urlLinkId) {
    return false;
  }
  return true;
}

/** ngrok free returns an HTML interstitial unless this header is present. */
const isNgrokBaseUrl = (url: string): boolean =>
  /ngrok(-free)?\.(app|dev)/i.test(url);

const applyNgrokDevHeaders = (
  headers: Record<string, string>,
  baseUrl: string
): void => {
  if (isNgrokBaseUrl(baseUrl)) {
    headers["ngrok-skip-browser-warning"] = "true";
  }
};

/**
 * Axios instance for authenticated widget API calls (x-link-id header).
 */
const createWidgetApiInstance = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: config.API_BASE_URL,
  });

  instance.interceptors.request.use((requestConfig) => {
    applyNgrokDevHeaders(
      requestConfig.headers as Record<string, string>,
      config.API_BASE_URL
    );
    // Bypass a disk-cached 410 on header-only GETs (catalog URL has no linkId).
    const method = (requestConfig.method ?? "get").toLowerCase();
    if (method === "get") {
      requestConfig.headers["Cache-Control"] = "no-cache";
      requestConfig.headers["Pragma"] = "no-cache";
    }
    const urlLinkId = resolveUrlLinkId();
    const persisted =
      useSessionStore.getState().session.data?.linkId ?? null;
    if (urlLinkId && currentLinkId !== urlLinkId) {
      setWidgetLinkId(urlLinkId);
    }
    const header = resolveXLinkIdHeader({
      urlLinkId,
      currentLinkId,
      persistedLinkId: persisted,
    });
    if (header) {
      requestConfig.headers["x-link-id"] = header;
    }
    // Bypass ngrok's free-tier interstitial ("ERR_NGROK_6024") so XHR calls
    // reach the backend instead of getting the HTML splash page. Only needed
    // when the API is tunneled through ngrok (dev); harmless otherwise.
    if (config.API_BASE_URL.includes("ngrok")) {
      requestConfig.headers["ngrok-skip-browser-warning"] = "true";
    }
    return requestConfig;
  });

  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      const errorCode = error?.response?.data?.error?.code;
      const statusCode = error?.response?.status;

      if (errorCode === "USER_BLOCKED") {
        window.dispatchEvent(new CustomEvent(USER_BLOCKED_EVENT));
      }

      if (errorCode === LINK_ERROR_CODE.SERVICE_UNAVAILABLE_MAINTENANCE) {
        window.dispatchEvent(
          new CustomEvent(MAINTENANCE_EVENT, {
            detail: error?.response?.data?.error?.details,
          }),
        );
      }

      if (
        (DEAD_LINK_CODES.includes(errorCode) || statusCode === 410) &&
        shouldBroadcastSessionExpired(error?.config)
      ) {
        window.dispatchEvent(
          new CustomEvent(SESSION_EXPIRED_EVENT, {
            detail: {
              code: errorCode || LINK_ERROR_CODE.LINK_EXPIRED,
              details: error?.response?.data?.error?.details,
            },
          })
        );
      }

      if (
        isAxiosNetworkFailure(error) &&
        error.config?.raasSilentOffline !== true
      ) {
        useOfflineStore.getState().reportOffline();
      }

      return Promise.reject(error);
    }
  );

  return instance;
};

export const widgetApi = createWidgetApiInstance();

export const saveContactPhone = async (
  linkId: string,
  phone: string,
  locale: string
): Promise<{ hasPhoneNumber: boolean; phoneCountry?: string }> => {
  const { data } = await widgetApi.put(
    `/widget/links/${linkId}/contact/phone`,
    { phone, locale }
  );
  return data.data;
};


/**
 * Device info to send with the claim request.
 */
export interface IDeviceInfo {
  userAgent?: string;
  language?: string;
  timezone?: string;
}

/**
 * Claim a magic link by linkId.
 * POST /widget/links/:linkId/claim
 *
 * The response contains the partner configuration — including the theme that
 * the widget must apply.
 */
export const claimLink = async (
  linkId: string,
  deviceInfo?: IDeviceInfo,
  lang?: string,
  seonSession?: string
): Promise<IWidgetSession> => {
  const apiBaseUrl = config.API_BASE_URL;

  try {
    const { data } = await axios.post(
      `${apiBaseUrl}/widget/links/${linkId}/claim`,
      {
        deviceInfo,
        ...(seonSession ? { seonSession } : {}),
      },
      {
        params: lang ? { lang } : undefined,
        ...(apiBaseUrl.includes("ngrok") && {
          headers: { "ngrok-skip-browser-warning": "true" },
        }),
      }
    );

    return {
      sessionId: data.data.sessionId,
      linkId: data.data.linkId,
      token: data.data.token,
      status: data.data.status,
      expiresAt: data.data.expiresAt,
      callbackUrl: data.data.callbackUrl,
      partner: {
        partnerId: data.data.partner.partnerId,
        name: data.data.partner.name,
        primaryColor: data.data.partner.primaryColor,
        theme: data.data.partner.theme,
        supportEmail: data.data.partner.supportEmail,
        supportWhatsappNumber: data.data.partner.supportWhatsappNumber,
        sessionExpiredRedirectUrl: data.data.partner.sessionExpiredRedirectUrl,
      },
      userData: data.data.userData,
      user: data.data.user,
      userStatus: data.data.userStatus
        ? {
            ...data.data.userStatus,
            cipStatus:
              data.data.userStatus.cipStatus ?? data.data.user?.cipStatus,
          }
        : data.data.user?.cipStatus
          ? {
              blocked: Boolean(data.data.user.blocked),
              suspended: Boolean(data.data.user.suspended),
              cipStatus: data.data.user.cipStatus,
            }
          : undefined,
      config: data.data.config,
      language: data.data.language,
      flavor: data.data.flavor,
      themeMode: data.data.themeMode,
      termsAccepted: data.data.termsAccepted,
      termsAcceptedAt: data.data.termsAcceptedAt,
      terms: data.data.terms,
    };
  } catch (error) {
    const status = isAxiosError(error) ? error.response?.status : undefined;
    const apiError = getWidgetApiError(error);
    const code = resolveLinkErrorCode(status, apiError?.code);

    // The message is only a diagnostic fallback: the error screen resolves the
    // user-facing copy from `code` via i18n.
    throw new LinkClaimError(
      apiError?.message || `Failed to claim link (${code})`,
      code,
      status,
      apiError?.details,
    );
  }
};

/**
 * Record the Terms & Conditions / E-Sign acceptance for the current user.
 * Persisted at the user level by the backend so the same user is not prompted
 * again on future links.
 * POST /widget/links/:linkId/accept-terms
 */
export interface IAcceptTermsResponse {
  termsAccepted: boolean;
  termsAcceptedAt?: string;
  termsAcceptedVersion?: string;
}

export const acceptTerms = async (
  linkId: string,
  options?: {
    version?: string;
    documents?: string[];
    acceptedEntryIds?: string[];
    language?: string;
  }
): Promise<IAcceptTermsResponse> => {
  const deviceInfo: IDeviceInfo = {
    userAgent: navigator.userAgent,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };

  const { data } = await widgetApi.post(
    `/widget/links/${linkId}/accept-terms`,
    {
      version: options?.version,
      documents: options?.documents,
      acceptedEntryIds: options?.acceptedEntryIds,
      language: options?.language ?? i18n.language,
      deviceInfo,
    }
  );

  return data.data;
};

export interface ILegalDocumentResponse {
  html: string;
}

/**
 * Fetch a legal document (E-Sign, Terms, etc.) as ready-to-render HTML.
 * GET /widget/legal/:doc/:lang
 */
export const getLegalDocument = async (
  doc: string,
  lang: string
): Promise<ILegalDocumentResponse> => {
  const { data } = await widgetApi.get(`/widget/legal/${doc}/${lang}`);
  return data.data;
};

/**
 * Fetch the partner Terms & Conditions for the link in a specific language.
 * The backend serves manually authored per-language content and falls back to
 * English when the requested language is unavailable.
 * GET /widget/links/:linkId/terms?lang=xx
 */
export const getTerms = async (
  linkId: string,
  lang?: string
): Promise<ITermsBlock | null> => {
  const { data } = await widgetApi.get(`/widget/links/${linkId}/terms`, {
    params: lang ? { lang } : undefined,
  });
  return data.data?.terms ?? null;
};

/**
 * Fetch the cash-deposit context for the claimed link.
 * GET /widget/cash-deposit/context (x-link-id header).
 *
 * Returns the minimal data needed to process the deposit — the sender (who is
 * making the deposit) and, for operation-funded links, the amount.
 */
export const getCashDepositContext = async (): Promise<ICashDepositContext> => {
  const { data } = await widgetApi.get("/widget/cash-deposit/context");
  return data.data;
};

/**
 * List the cash-deposit funding locations (merchants) for the push-funding
 * corridor. The merchant `fixedLoadAmounts` drive the displayed amount limits.
 * GET /widget/operations/funding-locations
 */
export const getFundingLocations = async (
  state?: string
): Promise<IFundingMerchant[]> => {
  const { data } = await widgetApi.get("/widget/operations/funding-locations", {
    params: state ? { state } : undefined,
  });
  return data.data;
};

/**
 * Nearby physical stores for the locator map.
 * GET /widget/operations/funding-locations/stores
 */
export const getFundingStores = async (
  query: IFundingStoresQuery = {},
): Promise<IFundingStore[]> => {
  const params: Record<string, string> = {};
  if (query.lat != null && query.lng != null) {
    params.lat = String(query.lat);
    params.lng = String(query.lng);
  }
  if (query.merchantId) {
    params.merchantId = query.merchantId;
  }
  const { data } = await widgetApi.get(
    "/widget/operations/funding-locations/stores",
    { params },
  );
  return data.data;
};

/**
 * InComm barcode policy: whether the widget must ask for a chain, plus the
 * resolved wildcard used when POST /funding/barcode omits merchantId.
 * GET /widget/operations/funding-policy
 *
 * The same fields ship on GET /widget/cash-deposit/context — prefer that
 * bootstrap payload and only call this if context omitted them.
 */
export const getFundingPolicy = async (): Promise<IFundingPolicy> => {
  const { data } = await widgetApi.get("/widget/operations/funding-policy");
  return data.data;
};

/**
 * Resolves the linkId for path-parameterized widget endpoints. The x-link-id
 * header is added by the interceptor, but these routes also need it in the URL.
 */
const resolveLinkId = (): string => {
  const linkId = useSessionStore.getState().session.data?.linkId;
  if (!linkId) {
    throw new Error("Missing linkId: claim the link first");
  }
  return linkId;
};

/**
 * Generate a cash-deposit barcode for an operation parked in awaiting_funding.
 * The corridor mints the instrument (real `vrsBarCode` + TTL + amount limits);
 * the widget renders `data` as a CODE128 symbol. `refreshReason` replaces the
 * active barcode (backend 409 when Superadmin disabled it or the mismatch is stale).
 * POST /widget/links/:linkId/operations/:operationId/funding/barcode
 */
export const generateFundingBarcode = async (
  operationId: string,
  merchantId?: string,
  options: { refreshReason?: FundingBarcodeRefreshReason } = {},
): Promise<IFundingInstrument> => {
  const linkId = resolveLinkId();
  const { data } = await widgetApi.post(
    `/widget/links/${linkId}/operations/${operationId}/funding/barcode`,
    {
      ...(merchantId ? { merchantId } : {}),
      ...(options.refreshReason ? { refreshReason: options.refreshReason } : {}),
    },
  );
  return data.data;
};

/**
 * Fetch a single operation for SSE reconnect (missed fundingAttempts).
 * GET /widget/links/:linkId/operations/:operationId
 */
export const getOperation = async (
  operationId: string,
  options?: { raasSilentOffline?: boolean },
): Promise<IWidgetOperation> => {
  const linkId = resolveLinkId();
  const { data } = await widgetApi.get(
    `/widget/links/${linkId}/operations/${operationId}`,
    { raasSilentOffline: options?.raasSilentOffline },
  );
  return data.data;
};
